import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useNotificationSocket } from '../context/NotificationSocketContext.jsx';
import { assignmentUi, fillCount } from '../i18n/assignmentUi.js';
import { t } from '../i18n/translate.js';
import { formatDate } from '../utils/formatDate.js';
import { Modal } from './Modal.jsx';

const POPUP_TYPES = new Set([
  'task_assigned',
  'task_reassigned',
  'project_member_added',
  'project_important_change',
]);

export function AssignmentAckModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { subscribe } = useNotificationSocket();
  const locale = user?.ui_locale;
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiJson('/api/my/assignments/pending')
      .then(setPayload)
      .catch(() => setPayload(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => subscribe((notification) => {
    if (POPUP_TYPES.has(notification?.type)) load();
  }), [load, subscribe]);

  const items = payload?.assignments || [];
  if (!items.length) return null;

  const taskCount = items.filter((item) => item.source === 'task').length;
  const title = taskCount > 0
    ? fillCount(t(assignmentUi.newTasksTitle, locale), taskCount)
    : fillCount(t(assignmentUi.newAssignmentsTitle, locale), items.length);
  const overdue = payload?.overdue_summary;

  const acknowledge = async (thenTasks) => {
    setBusy(true);
    try {
      await apiJson('/api/my/assignments/acknowledge', { method: 'POST', body: { all: true } });
      setPayload({ assignments: [], overdue_summary: overdue });
      if (thenTasks) navigate('/admin/tasks?mine=1');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      dismissible={false}
      title={title}
      onClose={() => {}}
      size="lg"
      footer={(
        <>
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => void acknowledge(true)}>
            {t(assignmentUi.viewMyTasks, locale)}
          </button>
          <button type="button" className="btn-primary" disabled={busy} onClick={() => void acknowledge(false)}>
            {t(assignmentUi.acknowledgeContinue, locale)}
          </button>
        </>
      )}
    >
      <ul className="list-plain" style={{ margin: 0, padding: 0 }}>
        {items.map((item) => (
          <li key={`${item.source}-${item.id}`} className="panel" style={{ marginBottom: '0.5rem' }}>
            <strong>{item.title}</strong>
            <div className="muted small">
              {[item.client_name, item.project_name].filter(Boolean).join(' — ')}
              {item.due_date ? ` · ${formatDate(item.due_date)}` : ''}
              {item.priority ? ` · ${item.priority}` : ''}
            </div>
            {item.assigned_by ? (
              <div className="muted small">{t(assignmentUi.assignedBy, locale).replace('{name}', item.assigned_by)}</div>
            ) : null}
            {item.role_label ? (
              <div className="muted small">{t(assignmentUi.role, locale).replace('{role}', item.role_label)}</div>
            ) : null}
          </li>
        ))}
      </ul>
      {overdue?.count > 0 ? (
        <div style={{ marginTop: '0.75rem' }}>
          <strong>{fillCount(t(assignmentUi.overdueSummary, locale), overdue.count)}</strong>
          <ul className="muted small">
            {(overdue.tasks || []).map((task) => (
              <li key={task.id}>{task.title}{task.due_date ? ` · ${formatDate(task.due_date)}` : ''}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </Modal>
  );
}
